import * as React from 'react';
import * as DateFns from 'date-fns';
import shouldFlipBG from '../utils/should-flip-bg';
import '../css/guide-element.sass';
import Card from './Card';

export interface Station {
  name: string;
  startTime: string;
  endTime: string;
}

interface GuideElementProps {
  title: string;
  startTime: string;
  endTime: string;
  theme?: 'night' | 'day';
  prevSlot?: Station;
  cellPosition: string;
  onGuideElementKeyDown: Function;
  timeLineEndDate: Date;
  channelName: string;
}

interface GuideElementState {
  flipBg?: string;
}

export const initialDate = () => {
  const newDate = new Date();
  newDate.setHours(newDate.getHours());
  newDate.setMinutes(0, 0, 0);
  return newDate;
};

export const convertUTCtoLocalTime = (time: string) => {
  return new Date(`${time} UTC`);
};

const timeSlotWidth = (
  endDateTime: Date,
  startDateTime: Date,
  timeLineEndDate: Date
) => {
  const now = initialDate();
  let minutes = DateFns.differenceInMinutes(startDateTime, endDateTime);
  if (now.getTime() - startDateTime.getTime() > 0) {
    minutes += DateFns.differenceInMinutes(now, startDateTime);
  }
  if (endDateTime.getTime() > timeLineEndDate.getTime()) {
    minutes -= DateFns.differenceInMinutes(timeLineEndDate, endDateTime);
  }
  return Math.abs(parseFloat((minutes / 5).toFixed()));
};

const emptyTimeSlot = (
  startTime: Date,
  timeLineEndDate: Date,
  prevSlot?: Station
) => {
  let emptySlotStartTime = initialDate();
  if (emptySlotStartTime.getTime() - startTime.getTime() > 0) {
    return <div className="guide-width-0"></div>;
  }
  if (prevSlot) {
    emptySlotStartTime = convertUTCtoLocalTime(prevSlot.endTime);
  }
  return (
    <div
      className={` guide-width-${timeSlotWidth(
        emptySlotStartTime,
        startTime,
        timeLineEndDate
      )}`}
    ></div>
  );
};

export default function GuideElement(props: GuideElementProps) {
  const { theme = 'day' } = props;
  const inputElParent = React.useRef(null);
  const [state, setState] = React.useState<GuideElementState>({
    flipBg: undefined
  });

  React.useEffect(() => {
    if (!state.flipBg && shouldFlipBG(inputElParent, theme)) {
      setState({
        ...state,
        flipBg: ' flipbg'
      });
    } else {
      setState({
        ...state,
        flipBg: ''
      });
    }
  }, []);

  const handleKeyDown = (e: any) => {
    props.onGuideElementKeyDown(e);
  };

  const startDateTime = convertUTCtoLocalTime(props.startTime);
  const endDateTime = convertUTCtoLocalTime(props.endTime);
  const startTime = DateFns.format(startDateTime, 'hh:mm a');
  const endTime = DateFns.format(endDateTime, 'hh:mm a');

  return (
    <div style={{ display: 'flex' }}>
      {emptyTimeSlot(startDateTime, props.timeLineEndDate, props.prevSlot)}
      <div
        id={`guide-element${props.cellPosition}`}
        role="button"
        className={`guide-element${
          state.flipBg
        } ${theme} guide-width-${timeSlotWidth(
          endDateTime,
          startDateTime,
          props.timeLineEndDate
        )}`}
        onMouseDown={e => {
          e.preventDefault();
        }}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <Card theme={theme}>
          <div className="name">{props.title}</div>
          <div className="description">{`${startTime} - ${endTime}`}</div>
          <div className="sr-only">{`on ${props.channelName}`}</div>
        </Card>
      </div>
    </div>
  );
}
